import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Event, EventInsert } from '@/types/events';
import { generateSlug } from '@/utils/urlUtils';
import { toast } from 'sonner';

export const useEvents = (brandId?: string) => {
  return useQuery({
    queryKey: ['events', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('os_events')
        .select(`
          *,
          speaker:os_events_speakers!speaker_id(id, name, image_url),
          co_speaker:os_events_speakers!co_speaker_id(id, name, image_url)
        `)
        .eq('brand_id', brandId)
        .order('time', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as Event[];
    },
    enabled: !!brandId,
  });
};

export const useEvent = (eventId?: string) => {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const { data, error } = await supabase
        .from('os_events')
        .select(`
          *,
          speaker:os_events_speakers!speaker_id(*),
          co_speaker:os_events_speakers!co_speaker_id(*)
        `)
        .eq('id', eventId)
        .single();
      if (error) throw error;
      return data as unknown as Event;
    },
    enabled: !!eventId,
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EventInsert) => {
      const { data: user } = await supabase.auth.getUser();
      const baseSlug = generateSlug(payload.title?.trim() || 'event');
      let slug = baseSlug;
      let suffix = 1;
      let slugExists = true;
      while (slugExists) {
        const { data: existing } = await supabase
          .from('os_events')
          .select('id')
          .eq('brand_id', payload.brand_id)
          .eq('slug', slug)
          .maybeSingle();
        if (existing) {
          suffix += 1;
          slug = `${baseSlug}-${suffix}`;
        } else {
          slugExists = false;
        }
      }
      const { data, error } = await supabase
        .from('os_events')
        .insert({ ...payload, slug, user_id: user.user?.id ?? null } as never)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Event;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['events', data.brand_id] });
      toast.success('Event created');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to create event'),
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Event> }) => {
      let finalUpdates = { ...updates };
      const titleToUse = updates.title?.trim();
      const wantsSlugFromTitle =
        titleToUse &&
        (updates.slug === null || updates.slug === undefined);
      if (wantsSlugFromTitle) {
        const { data: current } = await supabase
          .from('os_events')
          .select('brand_id, slug')
          .eq('id', id)
          .single();
        const currentSlugEmpty = current?.slug == null || String(current?.slug).trim() === '';
        if (current?.brand_id && currentSlugEmpty) {
          const baseSlug = generateSlug(titleToUse);
          let slug = baseSlug;
          let suffix = 1;
          let slugExists = true;
          while (slugExists) {
            const { data: existing } = await supabase
              .from('os_events')
              .select('id')
              .eq('brand_id', current.brand_id)
              .eq('slug', slug)
              .neq('id', id)
              .maybeSingle();
            if (existing) {
              suffix += 1;
              slug = `${baseSlug}-${suffix}`;
            } else {
              slugExists = false;
            }
          }
          finalUpdates = { ...finalUpdates, slug };
        }
      }
      const { data, error } = await supabase
        .from('os_events')
        .update(finalUpdates as never)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Event;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['events', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['event', data.id] });
      toast.success('Event updated');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to update event'),
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('os_events').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast.success('Event deleted');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to delete event'),
  });
};
